import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Col } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';

import {
  DEFAULT_PAGINATION_LENGTH,
  refillRequestPageTypes,
} from '../../../config/app.config';
import { formatDate } from '../../../helpers/formatDate';

import PaginationComponent from '../../../shared/components/pagination/Pagination';
import HeadingText from '../../../shared/components/form/HeadingText';
import StandardTableToolbar from '../../../shared/components/table-toolbar/StandardTableToolbar';
import withRouter from '../../../shared/components/withRouter';

import {
  getAllRefillRequests,
  changeRefillRequestStatus,
} from '../../../redux/actions/refillRequestActions';
import { toggleLoading } from '../../../redux/actions/commonActions';

import { RefillRequestModal } from './RequestDetails';
import {
  StatusChangeModal,
  RefillRequestsTable,
  refillComponentsWrapperMap,
} from './Components';
import {
  RefillRequestModalProvider,
  StatusChangeModalProvider,
  getRefillRequestsHeaders,
} from './utils';
import {
  REFILL_DATE_SHORTCUTS,
  REFILL_FILTER_SEARCH_THRESHOLD,
  REFILL_OPEN_STATUSES,
  REFILL_SCOPE_OPTIONS,
  REFILL_SORT_OPTIONS,
  getProfileRefillSortOptions,
  getRefillFilterCategories,
} from './utils/refillToolbarConfig';
import { RefillRequestMessageModal } from '../../modals/RefillRequestMessage';

const initialParams = {
  search: '',
  timeFilter: null,
  sortOrder: -1,
  sortField: 'createdAt',
  offset: 0,
  limit: DEFAULT_PAGINATION_LENGTH,
  filters: {},
  range: {},
  scope: 'open',
};

function reducer(params, action) {
  switch (action.type) {
    case 'SET_SEARCH':
      return { ...params, search: action.payload, offset: 0 };
    case 'SET_TIME_FILTER':
      return {
        ...params,
        timeFilter: action.payload,
        offset: action.payload === 'range' ? params.offset : 0,
      };
    case 'SET_SORT': {
      const { field, order } = action.payload;
      return {
        ...params,
        sortField: field ?? params.sortField,
        sortOrder: order ?? params.sortOrder,
        offset: 0,
      };
    }
    case 'SET_FILTER':
      return {
        ...params,
        filters: {
          ...params.filters,
          [action.payload.type]: action.payload.filters,
        },
        scope: action.payload.scope ?? params.scope,
        offset: 0,
      };
    case 'SET_SCOPE':
      return {
        ...params,
        scope: action.payload.scope,
        filters: action.payload.status
          ? { ...params.filters, status: action.payload.status }
          : params.filters,
        offset: 0,
      };
    case 'CLEAR_ALL_FILTERS':
      return { ...params, filters: {}, offset: 0 };
    case 'SET_RANGE':
      return { ...params, range: action.payload, offset: 0 };
    case 'SET_LIMIT':
      return { ...params, limit: action.payload, offset: 0 };
    case 'SET_OFFSET':
      return { ...params, offset: action.payload };
    default:
      return params;
  }
}

function RefillRequests({ type = refillRequestPageTypes.default, parentId }) {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const petId = searchParams.get('pet');
  const isFacilityPage = type === refillRequestPageTypes.default;

  const facilityId = useSelector((state) => state.user?.profile?.facility?._id);
  const { updatedRefillRequest, refillRequestsList } = useSelector(
    (state) => state.refillRequests,
  );
  const allStatuses = useSelector(
    (state) => state.refillRequests.refillRequestStatuses || [],
  );
  const doctors = useSelector(
    (state) => state.staff.staffDoctorsByFacility || [],
  );

  const [params, localDispatch] = useReducer(reducer, initialParams);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [showUpdatedHint, setShowUpdatedHint] = useState(false);
  const updatedHintTimerRef = useRef(null);

  const isStatusFilterExist =
    params.filters.status && params.filters.status.length > 0;

  const hasAdditionalNarrowing =
    !!params.search?.trim() ||
    !!params.timeFilter ||
    Object.values(params.filters || {}).some(
      (value) => Array.isArray(value) && value.length > 0,
    );
  const emptyStateMessage = isFacilityPage
    ? hasAdditionalNarrowing
      ? 'No matching Refill Requests to display'
      : 'No Refill Requests to display'
    : 'No Refill Requests to display';

const filterCategories = useMemo(
  () =>
    getRefillFilterCategories({
      statuses: allStatuses,
      doctors,
    }),
  [allStatuses, doctors],
);

const profileSortOptions = useMemo(
  () => getProfileRefillSortOptions({ petId }),
  [petId],
);

useEffect(() => {
  if (!isFacilityPage && petId && params.sortField === 'petName') {
    localDispatch({
      type: 'SET_SORT',
      payload: { field: 'createdAt', order: -1 },
    });
  }
}, [petId, isFacilityPage, params.sortField]);

const getRefillRequests = useCallback(async () => {
    const formattedStartDate = formatDate(params.range.startDate);
    const formattedEndDate = formatDate(params.range.endDate);

    const { status, deliveryType, doctorId } = params.filters;

    await dispatch(
      getAllRefillRequests({
        facilityId,
        params: {
          sort: {
            field: params.sortField,
            order: +params.sortOrder,
          },
          search: params.search,
          status:
            status?.length > 0
              ? status
              : isFacilityPage && params.scope === 'open'
                ? REFILL_OPEN_STATUSES
                : null,
          doctorId: doctorId?.length > 0 ? doctorId : null,
          parentId,
          petId,
          deliveryType: deliveryType?.length > 0 ? deliveryType : null,
          timeFilter: {
            type: params.timeFilter,
            startDate: formattedStartDate,
            endDate: formattedEndDate,
          },
          limit: params.limit,
          offset: params.offset,
        },
      }),
    );
  }, [facilityId, parentId, petId, params, dispatch, isFacilityPage]);

  const updateTable = useCallback(async () => {
    setRows((prevRows) => {
      return prevRows.map((row) => {
        if (row.id === updatedRefillRequest?._id) {
          const { status, statusHistory } = updatedRefillRequest;
          return {
            ...row,
            status: status.label,
            statusId: status._id,
            statusHistory,
          };
        }
        return row;
      });
    });
  }, [updatedRefillRequest]);

  useEffect(() => {
    const fetchRefillRequests = async () => {
      if (facilityId) {
        dispatch(toggleLoading(true));
        await getRefillRequests();
        dispatch(toggleLoading(false));
      }
    };

    fetchRefillRequests();
  }, [facilityId, getRefillRequests, dispatch]);

  useEffect(() => {
    setRows(refillRequestsList?.data || []);
    setTotal(refillRequestsList?.total || 0);
  }, [refillRequestsList]);

  useEffect(() => {
    if (!isStatusFilterExist) updateTable();
  }, [updateTable, isStatusFilterExist]);

  useEffect(() => {
    return () => {
      if (updatedHintTimerRef.current) {
        clearTimeout(updatedHintTimerRef.current);
      }
    };
  }, []);

  const handleTimeFilterChange = (timeFilter) => {
    localDispatch({ type: 'SET_TIME_FILTER', payload: timeFilter });
  };

  const handleSort = ({ field, order }) => {
    localDispatch({ type: 'SET_SORT', payload: { field, order } });
  };

  const handleFilter = (filterType, filters) => {
  localDispatch({
    type: 'SET_FILTER',
    payload: {
      type: filterType,
      filters,
    },
  });
};

const handleScopeChange = (scope) => {
  localDispatch({ type: 'SET_SCOPE', payload: { scope } });
};

  const handleClearAllFilters = () => {
    localDispatch({ type: 'CLEAR_ALL_FILTERS' });
  };

  const handlePageLimit = (newLimit) => {
    localDispatch({ type: 'SET_LIMIT', payload: newLimit });
  };

  const handleSearchChange = (search) => {
    localDispatch({ type: 'SET_SEARCH', payload: search });
  };

  const handleRefillRequestStatusChange = async (
    newStatusId,
    prescriptionId,
    comments,
    requiresResponse,
  ) => {
    dispatch(toggleLoading(true));
    await dispatch(
      changeRefillRequestStatus(
        newStatusId,
        prescriptionId,
        comments,
        requiresResponse,
      ),
    );
    await getRefillRequests();
    dispatch(toggleLoading(false));
  };

  const handleRefetchRefillRequest = async (withLoading = true) => {
    if (withLoading) {
      dispatch(toggleLoading(true));
    }

    await getRefillRequests();

    if (withLoading) {
      dispatch(toggleLoading(false));
    }

    if (isFacilityPage && withLoading) {
      setShowUpdatedHint(true);
      if (updatedHintTimerRef.current) {
        clearTimeout(updatedHintTimerRef.current);
      }
      updatedHintTimerRef.current = setTimeout(() => {
        updatedHintTimerRef.current = null;
        setShowUpdatedHint(false);
      }, 1500);
    }
  };

  const Wrapper = refillComponentsWrapperMap[type];

  return (
    <RefillRequestModalProvider
      handleRefetchRefillRequest={handleRefetchRefillRequest}
    >
      <StatusChangeModalProvider
        handleStatusChange={handleRefillRequestStatusChange}
      >
        <Col
          md={12}
          lg={12}
          className="manage-patient-container admin-container"
        >
          <Wrapper>
{isFacilityPage ? (
  <div className="sticky-table">
    <div className="ptb-board-header">
      <div className="page-title-with-action ptb-board-header__title-row">
        <HeadingText text="Refill Requests" />
        <Button
          type="button"
          className="btn-utility"
          onClick={() => handleRefetchRefillRequest(true)}
        >
          Refresh
        </Button>
        {showUpdatedHint ? (
          <span style={{ fontSize: '12px', color: '#999' }}>
            Updated!
          </span>
        ) : null}
      </div>
      <div className="ptb-board-header__subtitle-row">
        <div className="ptb-board-header__subtitle-stack">
          <div className="ptb-board-header__subtitle">
            Refill requests from Pet Parents
          </div>
          <div className="ptb-board-header__subtitle">
            submitted through the Loyal Pet mobile app
          </div>
        </div>
      </div>
    </div>
    <div className="refill-requests-toolbar">
      <StandardTableToolbar
        searchValue={params.search}
        onSearchChange={handleSearchChange}
        searchAutofocus={true}
        scope={isStatusFilterExist ? null : params.scope}
        onScopeChange={handleScopeChange}
        scopeOptions={REFILL_SCOPE_OPTIONS}
        sortField={params.sortField}
        sortOrder={params.sortOrder}
        onSortChange={handleSort}
        sortOptions={REFILL_SORT_OPTIONS}
        filters={params.filters}
        onFilterChange={handleFilter}
        onClearAllFilters={handleClearAllFilters}
        filterCategories={filterCategories}
        filterSearchThreshold={REFILL_FILTER_SEARCH_THRESHOLD}
        timeFilter={params.timeFilter}
        onTimeFilterChange={handleTimeFilterChange}
        dateShortcuts={REFILL_DATE_SHORTCUTS}
      />
    </div>
  </div>
) : (
  <div className="refill-requests-toolbar refill-requests-toolbar--profile">
    <StandardTableToolbar
      sortField={params.sortField}
      sortOrder={params.sortOrder}
      onSortChange={handleSort}
      sortOptions={profileSortOptions}
    />
  </div>
)}
            <RefillRequestsTable
              stickyTop="60px"
              heads={getRefillRequestsHeaders(type)}
              rows={rows}
              sortField={params.sortField}
              sortOrder={params.sortOrder}
              handleSort={handleSort}
              handleFilter={handleFilter}
              emptyStateMessage={emptyStateMessage}
            />
            <PaginationComponent
              handlePageLimit={handlePageLimit}
              nextPage={() =>
                localDispatch({
                  type: 'SET_OFFSET',
                  payload: params.offset + params.limit,
                })
              }
              prevPage={() =>
                localDispatch({
                  type: 'SET_OFFSET',
                  payload: params.offset - params.limit,
                })
              }
              gotoPage={(page) =>
                localDispatch({
                  type: 'SET_OFFSET',
                  payload: params.limit * page,
                })
              }
              limit={params.limit}
              offset={params.offset}
              total={total}
            />
          </Wrapper>
        </Col>
        <StatusChangeModal />
        <RefillRequestModal />
        <RefillRequestMessageModal />
      </StatusChangeModalProvider>
    </RefillRequestModalProvider>
  );
}

export default withRouter(RefillRequests);
